import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { autoScheduleApi } from '../../api/availability.ts';
import { useSession } from '../../state/session.tsx';
import { Button } from '../ui.tsx';
import { CLEANER_SKILLS, KM_PER_MILE, usesMiles, type CleanerSkill } from '../../../shared/cleaner-scheduling.ts';

/**
 * The rest of a cleaner's scheduling profile, beside their weekly hours: how
 * many jobs a day, how far they travel, and what work they are cleared for.
 * The auto-scheduler reads it when it proposes who takes a booking.
 */
export function SchedulingProfileCard({ userId, mayWrite }: { userId: string; mayWrite: boolean }) {
  const { t } = useTranslation();
  const { company, currentLocation } = useSession();
  const queries = useQueryClient();
  const miles = usesMiles(company?.countryCode);
  const key = ['scheduling-profile', company?.id, currentLocation?.id, userId];
  const profile = useQuery({ queryKey: key, queryFn: () => autoScheduleApi.profile(userId) });
  const [cap, setCap] = useState('');
  const [radius, setRadius] = useState('');
  const [skills, setSkills] = useState<CleanerSkill[]>([...CLEANER_SKILLS]);
  const [home, setHome] = useState({ addressLine1: '', city: '', state: '', postalCode: '' });
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  useEffect(() => {
    if (!profile.data) return;
    setCap(profile.data.maxJobsPerDay === null ? '' : String(profile.data.maxJobsPerDay));
    const km = profile.data.travelRadiusKm;
    setRadius(km === null ? '' : String(Math.round((miles ? km / KM_PER_MILE : km) * 10) / 10));
    setSkills(profile.data.skills);
    const saved = profile.data.homeAddress;
    setHome({
      addressLine1: saved?.addressLine1 ?? '',
      city: saved?.city ?? '',
      state: saved?.state ?? '',
      postalCode: saved?.postalCode ?? '',
    });
    setStatus('idle');
  }, [profile.data, miles]);

  const seesHome = profile.data?.homeAddress !== undefined;

  async function save() {
    if (!profile.data) return;
    setStatus('saving');
    try {
      const radiusValue = radius.trim() === '' ? null : Number(radius);
      const saved = await autoScheduleApi.saveProfile(userId, {
        revision: profile.data.revision,
        maxJobsPerDay: cap.trim() === '' ? null : Number(cap),
        travelRadiusKm:
          radiusValue === null ? null : Math.round((miles ? radiusValue * KM_PER_MILE : radiusValue) * 100) / 100,
        skills,
        // Only owners and managers get the home address back, and only they send it.
        ...(seesHome
          ? {
              homeAddress:
                home.addressLine1.trim() === ''
                  ? null
                  : {
                      addressLine1: home.addressLine1.trim(),
                      city: home.city.trim() || null,
                      state: home.state.trim() || null,
                      postalCode: home.postalCode.trim() || null,
                      countryCode: company?.countryCode ?? null,
                    },
            }
          : {}),
      });
      queries.setQueryData(key, saved);
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  }

  return (
    <section className="mt-5" aria-label={t('autoSchedule.profileTitle')}>
      <h3 className="font-semibold">{t('autoSchedule.profileTitle')}</h3>
      <p className="mb-2 text-sm">{t('autoSchedule.profileHint')}</p>
      {profile.isError && <Button onClick={() => void profile.refetch()}>{t('planning.retry')}</Button>}
      {profile.data && (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="field-label">
            {t('autoSchedule.maxJobs')}
            <input
              className="field-control w-full"
              type="number"
              inputMode="numeric"
              min={1}
              max={50}
              placeholder={t('autoSchedule.noLimit')}
              value={cap}
              disabled={!mayWrite}
              onChange={(e) => setCap(e.target.value)}
            />
          </label>
          <label className="field-label">
            {t(miles ? 'autoSchedule.radiusMiles' : 'autoSchedule.radiusKm')}
            <input
              className="field-control w-full"
              type="number"
              inputMode="decimal"
              min={1}
              placeholder={t('autoSchedule.noLimit')}
              value={radius}
              disabled={!mayWrite}
              onChange={(e) => setRadius(e.target.value)}
            />
          </label>
          <fieldset className="sm:col-span-2">
            <legend className="field-label">{t('autoSchedule.skills')}</legend>
            <div className="flex flex-wrap gap-4">
              {CLEANER_SKILLS.map((skill) => (
                <label key={skill} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={skills.includes(skill)}
                    disabled={!mayWrite}
                    onChange={(e) =>
                      setSkills(e.target.checked ? [...skills, skill] : skills.filter((s) => s !== skill))
                    }
                  />
                  {t(`autoSchedule.skill.${skill}`)}
                </label>
              ))}
            </div>
            {skills.length === 0 && <p className="field-hint">{t('autoSchedule.noSkills')}</p>}
          </fieldset>
          {seesHome && (
            <fieldset className="grid gap-2 sm:col-span-2 sm:grid-cols-2">
              <legend className="field-label">{t('autoSchedule.homeAddress')}</legend>
              <p className="field-hint sm:col-span-2">{t('autoSchedule.homeHint')}</p>
              {(
                [
                  ['addressLine1', 'autoSchedule.homeStreet'],
                  ['city', 'autoSchedule.homeCity'],
                  ['state', 'autoSchedule.homeState'],
                  ['postalCode', 'autoSchedule.homePostalCode'],
                ] as const
              ).map(([field, label]) => (
                <label key={field} className="field-label">
                  {t(label)}
                  <input
                    className="field-control w-full"
                    autoComplete="off"
                    value={home[field]}
                    disabled={!mayWrite}
                    onChange={(e) => setHome({ ...home, [field]: e.target.value })}
                  />
                </label>
              ))}
              {profile.data.homeAddress && !profile.data.homeAddress.located && (
                <p className="field-hint sm:col-span-2">{t('autoSchedule.homeNotLocated')}</p>
              )}
            </fieldset>
          )}
          <div className="flex items-center gap-3 sm:col-span-2">
            <Button variant="primary" disabled={!mayWrite} busy={status === 'saving'} onClick={() => void save()}>
              {t('autoSchedule.save')}
            </Button>
            {status === 'saved' && <span role="status">{t('autoSchedule.saved')}</span>}
            {status === 'error' && <span role="alert">{t('autoSchedule.saveFailed')}</span>}
          </div>
        </div>
      )}
    </section>
  );
}
